import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Dimmer, Loader, Modal } from 'semantic-ui-react';

import { getProgramSubmissionTemplatesByType } from '../../../../../../api/nora/programs';
import { getSubmissionHistory } from '../../../../../../api/nora/submissions';

import useForm from '../../../../../../hooks/useForm';

import { useUIAction } from '../../../../../../contexts/UIContext';
import { useProgramState } from '../../../../../../contexts/ProgramsContext';

import FormSelect from '../../../../../Form/FormSelect';

const SubmissionSearchModal = ( {
	closeModal,
	onSearch,
	open,
	setFilter
} ) => {
	const { program } = useProgramState();
	const { setApiErrorAlert } = useUIAction();

	const [ isLoading, setIsLoading ] = useState( false );
	const [ searchLoading, setSearchLoading ] = useState( false );
	const [ submissions, setSubmissions ] = useState( [] );
	const [ instances, setInstances ] = useState( [] );

	const { formData, updateField, setFormData } = useForm( {} );

	useEffect( () => {
		( async () => {
			try {
				setIsLoading( true );

				const submissions = await getProgramSubmissionTemplatesByType( program.id, 0 );

				const mappedSubmissions = submissions.map( ( { id, name } ) => ( {
					key: id,
					text: name,
					value: id
				} ) );

				setSubmissions( mappedSubmissions );
			} catch ( error ) {
				setApiErrorAlert( error );
			}

			setIsLoading( false );
		} )();
	}, [
		program.id,
		setApiErrorAlert
	] );

	useEffect( () => {
		if ( formData.submission ) {
			( async () => {
				setIsLoading( true );

				try {
					const instances = await getSubmissionHistory( formData.submission );

					const mappedInstances = instances.types.map( ( { id, name } ) => ( {
						key: id,
						text: name,
						value: id
					} ) );

					setInstances( mappedInstances );
				} catch ( error ) {
					setApiErrorAlert( error );
				}

				setIsLoading( false );
			} )();
		}
	}, [
		formData.submission,
		setApiErrorAlert
	] );

	const handleClose = useCallback( () => {
		setFormData( {} );
		closeModal();
	}, [ closeModal, setFormData ] );

	const handleSearch = useCallback( async () => {
		setSearchLoading( true );
		await onSearch( formData.instance );

		const submission = instances
			.find( ( { value } ) => value === formData.instance )
			?.text;

		setFilter( <span>Showing the status of <strong>{ submission }</strong> from all agencies</span> );
		setFormData( {} );
		setSearchLoading( false );
		closeModal();
	}, [
		closeModal,
		formData.instance,
		instances,
		onSearch,
		setFilter,
		setFormData
	] );

	return (
		<Modal
			open={ open }
			size="tiny"
		>
			<Modal.Header>Search By Submission</Modal.Header>
			<Modal.Content>
				<Dimmer
					active={ isLoading }
					inverted
					style={{ borderRadius: 0 }}
				>
					<Loader inverted />
				</Dimmer>
				<FormSelect
					fieldLabel="Submission"
					name="submission"
					onChange={ updateField }
					options={ submissions }
					value={ formData.submission }
					width={ 8 }
				/>
				<FormSelect
					disabled={ !formData.submission || !instances.length }
					fieldLabel="Instance"
					name="instance"
					onChange={ updateField }
					options={ instances }
					value={ formData.instance }
					width={ 8 }
				/>
			</Modal.Content>
			<Modal.Actions>
				<Button
					content="Search"
					onClick={ handleSearch }
					disabled={ !( formData.submission && formData.instance ) }
					loading={ searchLoading }
				/>
				<Button
					content="Cancel"
					onClick={ handleClose }
				/>
			</Modal.Actions>
		</Modal>
	);
};

SubmissionSearchModal.propTypes = {
	closeModal: PropTypes.func.isRequired,
	onSearch: PropTypes.func.isRequired,
	open: PropTypes.bool,
	setFilter: PropTypes.func.isRequired
};

export default SubmissionSearchModal;
